/* eslint-disable @typescript-eslint/no-use-before-define */

import { useCallback, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useBreakpointValue } from '@chakra-ui/react';
import { Elements } from '@stripe/react-stripe-js';

import asBentoContainer from 'common/bento/hoc/asBentoContainer';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import { useConsumeQueryParam } from 'common/bento/lib/useConsumeQueryParam';
import useXStateDevtools from 'common/bento/lib/useXStateDevtools';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import useCompanyContext from 'common/hooks/useCompanyContext';
import useCreateNewAccount from 'features/CreateNewAccount/hooks/useCreateNewAccount';
import OnboardingCapitalDeposit from 'features/Onboarding/flows/capitalDeposit/CapitalDeposit';
import OnboardingSasuCreation from 'features/Onboarding/flows/sasuCreation/SasuCreation';
import ProfileHelper from 'features/Onboarding/pages/ProfileHelper';
import stripePromise from 'features/Stripe/utils/stripe';

import { savePartner } from './common/localStorage/partner';
import OBLoaderPage from './components/OBLoaderPage/OBLoaderPage';
import Beneficiary from './flows/beneficiary/Beneficiary';
import OnboardingCapitalDepositMandatee from './flows/capitalDepositMandatee/CapitalDepositMandatee';
import InvitedAccountHolderCapitalDeposit from './flows/invitedAccountHolderCapitalDeposit/InvitedAccountHolderCapitalDeposit';
import OnboardingShineStart from './flows/shineStart/ShineStart';
import OnboardingStandard from './flows/standard';
import { State as StandardState } from './flows/standard/machine';
import { OnboardingCompanyDocument } from './graphql/queries/onboardingCompany.gql';
import useApplicationTypeStore, {
  getApplicationTypeFromQuery,
} from './hooks/useApplicationTypeStore';
import { useClearAbortedApplication } from './hooks/useClearAbortedApplication';
import useOnboardingUser from './hooks/useUserProfile';
import OnboardingDesktop from './layout/OnboardingDesktop';
import OnboardingMobile from './layout/OnboardingMobile';
import { ApplicationTypeContext } from './lib/applicationType';
import machineConfig, { model, State } from './machine';
import ApplicationSelectionMandatee from './pages/ApplicationSelectionMandatee';
import CompanyCreationLandbotPage from './pages/CompanyCreationLandbot';
import CompanyCreationLegalForm from './pages/CompanyCreationLegalForm';
import ProfileHelperMandatee from './pages/ProfileHelperMandatee';
import RedirectBeneficiaryInvitee from './pages/RedirectBeneficiaryInvitee';
import SasuCreationEligibility from './pages/SasuCreationEligibility';
import WelcomeMandatee from './pages/WelcomeMandatee';
import { ApplicationType } from './types';

const Onboarding = asBentoContainer(
  () => {
    const initializedApplicationType = useRef(false);
    const applicationTypeStore = useApplicationTypeStore();
    const applicationTypeContext = {
      applicationType: applicationTypeStore.value,
      dispatch: applicationTypeStore.dispatch,
    };

    useXStateDevtools();

    /**
     * If coming from a capital deposit partner URL (eg. LegalPlace),
     * save the information and go to the Capital Deposit flow.
     */
    useConsumeQueryParam('partner', (partner) => {
      savePartner({ isConfirmedPartner: true, partner });
      applicationTypeContext.dispatch(ApplicationType.CapitalDeposit);
    });

    const companyContext = useCompanyContext();
    const { companyProfileId } = companyContext;

    const userQuery = useOnboardingUser();

    const companyQuery = useQuery(OnboardingCompanyDocument, {
      onCompleted: ({ viewer }) => {
        if (!initializedApplicationType.current) {
          initializedApplicationType.current = true;

          const applicationType = getApplicationTypeFromQuery({
            company: viewer.company,
          });

          if (applicationType) {
            applicationTypeContext.dispatch(applicationType);
          }
        }
      },
      onError: (error) => {
        throw error;
      },
      skip: !companyProfileId,
      variables: {
        companyProfileId: companyProfileId ?? '',
      },
    });

    const isMobile = useBreakpointValue({ base: true, md: false }) as boolean;
    const [createNewAccount] = useCreateNewAccount();

    if (userQuery.loading || !userQuery.data || companyQuery.loading) {
      return <OBLoaderPage onPrev={() => null} />;
    }

    return (
      <Elements stripe={stripePromise}>
        <ApplicationTypeContext.Provider value={applicationTypeContext}>
          <OnboardingComponent
            moduleData={{
              applicationTypeContext,
              companyContext,
              companyData: companyQuery.data
                ? {
                    ...companyQuery.data.viewer,
                    shouldAskSanctionsDeclarationQuestions:
                      companyQuery.data.shouldAskSanctionsDeclarationQuestions,
                  }
                : undefined,
              createNewAccount,
              isMobile,
              user: userQuery.data.viewer,
            }}
            onDone={() => {}}
          />
        </ApplicationTypeContext.Provider>
      </Elements>
    );
  },

  {
    layouts: {
      base: OnboardingMobile,
      md: OnboardingDesktop,
    },
  },
);

const OnboardingComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    const stringifiedState = stringifyState<State>(state);

    const onPrev = useCallback(() => {
      send(model.events.PREV());
    }, [send]);

    useClearAbortedApplication(onPrev, moduleData);

    switch (stringifiedState) {
      case State.PROFILE_HELPER:
        return (
          <ProfileHelper
            onPrev={onPrev}
            onSelectBeneficiary={() => send(model.events.SELECT_BENEFICIARY())}
            onSelectCapitalDeposit={() =>
              send(model.events.SELECT_CAPITAL_DEPOSIT())
            }
            onSelectCompanyCreation={() =>
              send(model.events.SELECT_COMPANY_CREATION())
            }
            onSelectShineStart={() => send(model.events.SELECT_SHINE_START())}
            onSelectStandard={() => send(model.events.SELECT_STANDARD())}
          />
        );

      case State.BENEFICIARY: {
        if (!moduleData.companyData) {
          throw new Error('The beneficiary flow only works with a company');
        }

        return (
          <Beneficiary
            companyProfileId={moduleData.companyData.company.companyProfileId}
            onDone={() => send(model.events.PREV())}
          />
        );
      }

      case State.INVITED_ACCOUNT_HOLDER_CAPITAL_DEPOSIT:
        if (!moduleData.companyData) {
          throw new Error(
            'The invited account holder flow only works with a company',
          );
        }

        return (
          <InvitedAccountHolderCapitalDeposit
            companyProfileId={moduleData.companyData.company.companyProfileId}
            onDone={() => send(model.events.PREV())}
          />
        );

      case State.STANDARD:
        return (
          <OnboardingStandard
            onboardingData={moduleData}
            onDone={({ finalState }) => {
              if (finalState === StandardState.ABORT_TO_CAPITAL_DEPOSIT) {
                return send(model.events.SELECT_CAPITAL_DEPOSIT());
              }

              return send(model.events.PREV());
            }}
          />
        );

      case State.CAPITAL_DEPOSIT:
        return (
          <OnboardingCapitalDeposit
            applicationTypeContext={moduleData.applicationTypeContext}
            companyProfileId={moduleData.companyData?.company.companyProfileId}
            onDone={() => send(model.events.PREV())}
          />
        );

      case State.SASU_CREATION:
        if (!moduleData.companyData?.company.isCompanyCreationSASU) {
          throw new Error(
            'isCompanyCreationSASU should be true in sasu creation flow',
          );
        }

        return (
          <OnboardingSasuCreation
            companyProfileId={moduleData.companyData.company.companyProfileId}
            onDone={() => send(model.events.PREV())}
          />
        );

      case State.SHINE_START:
        return (
          <OnboardingShineStart
            applicationTypeContext={moduleData.applicationTypeContext}
            onDone={() => send(model.events.PREV())}
          />
        );

      case State.WELCOME_MANDATEE:
        return (
          <WelcomeMandatee
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );

      case State.APPLICATION_SELECTION_MANDATEE:
        return (
          <ApplicationSelectionMandatee
            onPrev={onPrev}
            onSelectPendingApplication={() =>
              send(model.events.SELECT_PENDING_MANDATEE_APPLICATION())
            }
            onSelectStartNewApplication={() =>
              send(model.events.SELECT_START_NEW_MANDATEE_APPLICATION())
            }
          />
        );

      case State.PROFILE_HELPER_MANDATEE:
        return (
          <ProfileHelperMandatee
            onPrev={onPrev}
            onSelectCapitalDeposit={() =>
              send(model.events.SELECT_CAPITAL_DEPOSIT_MANDATEE())
            }
          />
        );

      case State.CAPITAL_DEPOSIT_MANDATEE:
        return (
          <OnboardingCapitalDepositMandatee
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? send(model.events.PREV())
                : send(model.events.NEXT())
            }
          />
        );

      case State.REDIRECT_BENEFICIARY_INVITEE:
        return <RedirectBeneficiaryInvitee onPrev={onPrev} />;

      case State.COMPANY_CREATION_LEGAL_FORM:
        return (
          <CompanyCreationLegalForm
            onPrev={onPrev}
            onSubmit={(legalForm) =>
              send(model.events.SUBMIT_COMPANY_CREATION_LEGAL_FORM(legalForm))
            }
          />
        );

      case State.COMPANY_CREATION_LANDBOT:
        return (
          <CompanyCreationLandbotPage
            legalForm={state.context.companyLegalForm}
            onLandbotCallback={(event) => {
              if (event === 'goShineStartMicro') {
                send(model.events.SELECT_SHINE_START());
              } else if (event === 'goCapdep') {
                send(model.events.SELECT_CAPITAL_DEPOSIT());
              }
            }}
            onPrev={onPrev}
          />
        );

      case State.SASU_CREATION_ELIGIBILITY_CHECK:
        return (
          <SasuCreationEligibility
            onPrev={onPrev}
            onSubmit={() => send(model.events.SELECT_SASU_CREATION())}
          />
        );

      case State.SEND_NEW_MANDATEE_APPLICATION:
      case State.INIT:
      case State.INIT_MANDATEE:
      case State.INIT_NON_MANDATEE:
      case State.START_APPLICATION:
      case State.DONE:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },

  {
    devTools: true,
    machineConfig,
  },
);

export default Onboarding;
